/**
 * Prompt store — leaf module (Perf 3.1.b Step 5).
 */
import { create } from 'zustand';
import { takeInitial } from '../../shared/public/catalog.cjs';
import { promptsApi, promptCategoriesApi } from '@/lib/api/prompts';

const usePromptStore = create((set, get) => ({
  promptsBySlug: {},
  catalogSlugs: [],
  isLoadingCatalog: false,
  isLoadingMoreCatalog: false,
  catalogError: null,
  isFetchedCatalog: false,
  catalogActiveFilters: {},
  catalogPageSize: 20,
  catalogTotal: 0,
  catalogOffset: 0,
  catalogHasMore: false,

  featuredSlugs: [],
  isLoadingFeatured: false,
  isFetchedFeatured: false,

  detailLoadingBySlug: {},
  detailErrorBySlug: {},

  isRunningBySlug: {},
  runErrorBySlug: {},

  categories: [],
  isLoadingCategories: false,
  isFetchedCategories: false,
  categoriesError: null,

  getPromptBySlug: (slug) => get().promptsBySlug[slug] || null,

  upsertPrompts: (prompts) => {
    if (!Array.isArray(prompts) || !prompts.length) return;
    set(state => ({
      promptsBySlug: {
        ...state.promptsBySlug,
        ...Object.fromEntries(prompts.filter(Boolean).map(p => [p.slug, { ...(state.promptsBySlug[p.slug] || {}), ...p }])),
      },
    }));
  },

  fetchCatalog: async (filters = {}) => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    );
    set({ isLoadingCatalog: true, catalogError: null });
    try {
      const pageSize = get().catalogPageSize;
      const params = { limit: pageSize, skip: 0, ...cleanFilters };
      const data = await (takeInitial('prompts', cleanFilters) || promptsApi.list(params));
      const prompts = data.prompts || [];
      const pagination = data.pagination || {};
      get().upsertPrompts(prompts);
      set({
        catalogSlugs: prompts.map(p => p.slug),
        catalogTotal: pagination.total ?? data.total ?? prompts.length,
        catalogHasMore: pagination.hasMore ?? false,
        catalogActiveFilters: Object.fromEntries(Object.entries(cleanFilters).filter(([key]) => key !== 'skip')),
        catalogOffset: Number(pagination.skip || 0),
        isFetchedCatalog: true,
        catalogError: null,
      });
      return prompts;
    } catch (error) {
      set({ catalogError: error.message || 'Failed to load prompts' });
      throw error;
    } finally {
      set({ isLoadingCatalog: false });
    }
  },

  loadMoreCatalog: async () => {
    const state = get();
    if (state.isLoadingMoreCatalog || state.isLoadingCatalog) return;
    if (!state.catalogHasMore) return;
    set({ isLoadingMoreCatalog: true });
    try {
      const params = {
        limit: state.catalogPageSize,
        skip: state.catalogOffset + state.catalogSlugs.length,
        ...state.catalogActiveFilters,
      };
      const data = await promptsApi.list(params);
      const prompts = data.prompts || [];
      const pagination = data.pagination || {};
      get().upsertPrompts(prompts);
      const existing = new Set(state.catalogSlugs);
      const additions = prompts.map(p => p.slug).filter(s => !existing.has(s));
      set({
        catalogSlugs: [...state.catalogSlugs, ...additions],
        catalogTotal: pagination.total ?? state.catalogTotal,
        catalogHasMore: pagination.hasMore ?? false,
      });
    } catch (error) {
      set({ catalogError: error.message || 'Failed to load more prompts' });
    } finally {
      set({ isLoadingMoreCatalog: false });
    }
  },

  fetchFeatured: async (limit = 8) => {
    if (get().isLoadingFeatured) return;
    set({ isLoadingFeatured: true });
    try {
      const data = await promptsApi.list({ featured: true, limit });
      const prompts = data.prompts || [];
      get().upsertPrompts(prompts);
      set({ featuredSlugs: prompts.map(p => p.slug), isFetchedFeatured: true });
    } catch {
      set({ isFetchedFeatured: true });
    } finally {
      set({ isLoadingFeatured: false });
    }
  },

  fetchPromptBySlug: async (slug, { force = false } = {}) => {
    if (!slug) return null;
    const cached = get().promptsBySlug[slug];
    if (!force && cached?.fields) {
      const isMedia = cached.outputType === 'image' || cached.outputType === 'video';
      const hasPicker = !!(cached.defaultModel?.id || (cached.suggestedModels?.length > 0));
      // Detail GET enriches media pipelines with capability-matched models; don't
      // serve a stale cache that only has list/catalog fields.
      if (!isMedia || hasPicker) return cached;
    }
    set(state => ({
      detailLoadingBySlug: { ...state.detailLoadingBySlug, [slug]: true },
      detailErrorBySlug: { ...state.detailErrorBySlug, [slug]: null },
    }));
    try {
      const data = await promptsApi.getBySlug(slug);
      const prompt = data.prompt;
      if (prompt) get().upsertPrompts([prompt]);
      return prompt;
    } catch (error) {
      set(state => ({
        detailErrorBySlug: { ...state.detailErrorBySlug, [slug]: error.message || 'Failed to load prompt' },
      }));
      throw error;
    } finally {
      set(state => ({
        detailLoadingBySlug: { ...state.detailLoadingBySlug, [slug]: false },
      }));
    }
  },

  runPrompt: async (slug, payload = {}) => {
    set(state => ({
      isRunningBySlug: { ...state.isRunningBySlug, [slug]: true },
      runErrorBySlug: { ...state.runErrorBySlug, [slug]: null },
    }));
    try {
      const data = await promptsApi.run(slug, payload);
      return data.conversationId || null;
    } catch (error) {
      set(state => ({
        runErrorBySlug: { ...state.runErrorBySlug, [slug]: error.message || 'Prompt run failed' },
      }));
      throw error;
    } finally {
      set(state => ({
        isRunningBySlug: { ...state.isRunningBySlug, [slug]: false },
      }));
    }
  },

  fetchCategories: async () => {
    if (get().isLoadingCategories) return;
    set({ isLoadingCategories: true, categoriesError: null });
    try {
      const data = await promptCategoriesApi.list();
      const categories = data.promptCategories || [];
      set({ categories, isFetchedCategories: true });
      return categories;
    } catch (error) {
      set({ categoriesError: error.message || 'Failed to load categories' });
      throw error;
    } finally {
      set({ isLoadingCategories: false });
    }
  },

  reset: () => set({
    promptsBySlug: {},
    catalogSlugs: [],
    catalogOffset: 0,
    isLoadingCatalog: false,
    catalogError: null,
    isFetchedCatalog: false,
    catalogActiveFilters: {},
    featuredSlugs: [],
    isLoadingFeatured: false,
    isFetchedFeatured: false,
    detailLoadingBySlug: {},
    detailErrorBySlug: {},
    isRunningBySlug: {},
    runErrorBySlug: {},
    categories: [],
    isLoadingCategories: false,
    isFetchedCategories: false,
    categoriesError: null,
  }),
}));

export { usePromptStore };
export default usePromptStore;
